import type {FetchApiError} from '@stripe-internal/connect-embedded-lib';
import {
  getFormURLEncodedBody,
  handleFetchResponse,
} from '@stripe-internal/connect-embedded-lib';
import {getStripeApiHost} from '../data-layer-client/buildStripeClient';
import {ACCOUNT_HEADER, LIVEMODE_HEADER} from '../utils/headerConstants';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import {authStore} from './authentication/AuthenticationStore';
import {getDataLayerHeaders} from './proxyRequest';
import type {
  IFrameLogoutRequest,
  IFramePopupAuthenticationComplete,
} from '../data-layer-client/FrameMessenger';
import type {AccountSessionClaim} from './types';
import type {SerializedFetchError} from '../data-layer-client/utils';
import {serializeFetchErrorForPostMessage} from '../data-layer-client/utils';

type InvalidateSessionStatus = {
  invalidated: boolean;
  error?: SerializedFetchError;
};

/**
 * Invalidate sessions and clear API keys.
 * @param param0 override params for request
 * @param clearUserSessionOnly clear user session and keep the account session, used only for dev utils
 * @returns response.invalidated indicating auth state has changed as a result of this invalidation,
 * response.error indicating the API request is responded with error
 */
export async function invalidateSessions(
  iframeRequest: IFrameLogoutRequest,
): Promise<InvalidateSessionStatus> {
  const {request} = iframeRequest;
  const {
    clearUserSessionOnly = false,
    merchantIdOverride,
    livemodeOverride,
  } = request;
  const keyAndType = authStore.getApiKey('stripe_user');

  let sessionsInvalidated = true;
  let error: FetchApiError | undefined;
  if (keyAndType) {
    const {serviceEnvironment} = getCurrentScriptUrlContext();
    const requestUrl = `https://${getStripeApiHost(
      serviceEnvironment,
    )}/v1/connect/session`;

    let headers: HeadersInit = {
      Authorization: `Bearer ${keyAndType.key}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    };
    headers = getDataLayerHeaders(headers);

    if (merchantIdOverride) {
      headers[ACCOUNT_HEADER] = merchantIdOverride;
    }
    if (livemodeOverride !== undefined) {
      headers[LIVEMODE_HEADER] = livemodeOverride.toString();
    }
    const url = new URL(requestUrl);

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    try {
      await handleFetchResponse(response);
    } catch (e) {
      // if response status is 200, user session is invalidated successfully,
      // if status is 401 or 404, user session does not exist and is considered invalidated,
      // otherwise, user session is still present and logout is unsuccessful
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      error = e as FetchApiError;
      const status = error.error.httpStatus;
      if (status !== 401 && status !== 404) {
        sessionsInvalidated = false;
      }
    }
  }

  if (sessionsInvalidated) {
    authStore.clear(clearUserSessionOnly ? 'stripe_user' : undefined);
  }

  return {
    invalidated: sessionsInvalidated,
    error: error ? serializeFetchErrorForPostMessage(error) : undefined,
  };
}

type UserAuthedKeyPayload = {
  userAuthedEmbeddedKey: string;
  refreshToken: string;
  accountId: string;
  publishableKey?: string;
  accountSessionClaim?: AccountSessionClaim;
  livemode: boolean;
};

export async function createUserAuthedKey(
  accountSessionApiKey: string,
  iframeRequest: IFramePopupAuthenticationComplete,
): Promise<UserAuthedKeyPayload> {
  const {apiKey, sessionCreatedForEmbedded} = iframeRequest;
  const {serviceEnvironment} = getCurrentScriptUrlContext();
  const requestUrl = `https://${getStripeApiHost(
    serviceEnvironment,
  )}/v1/embedded/user_authed_key`;

  let headers: HeadersInit = {
    Authorization: `Bearer ${apiKey}`,
    'Content-Type': 'application/x-www-form-urlencoded',
  };
  headers = getDataLayerHeaders(headers);

  const url = new URL(requestUrl);

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: getFormURLEncodedBody({
      embedded_api_key: accountSessionApiKey,
      session_created_from_embedded: sessionCreatedForEmbedded,
    }),
  });

  const processedResponse = await handleFetchResponse(response);
  return {
    userAuthedEmbeddedKey: processedResponse.embedded_session_api_key,
    refreshToken: processedResponse.embedded_user_session_refresh_token,
    accountId: processedResponse.account_id,
    publishableKey: processedResponse.publishable_key,
    accountSessionClaim: processedResponse.account_session_claim,
    livemode: processedResponse.livemode,
  };
}
